import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Briefcase, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTheme } from '@/contexts/ThemeContext';

const YAS_LOGO = '/yas-logo.png';

const Register = ({ setUser }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'youth',
    email_owner: 'youth',
    work_site: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { colors, mode } = useTheme();

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        toast.success(`Welcome, ${data.user.name}! Your account is ready.`);
        
        // Route based on role
        const routes = {
          youth: '/dashboard',
          coach: '/coach',
          admin: '/admin',
          lead_monitor: '/lead-monitor',
          parent: '/parent'
        };
        navigate(routes[data.user.role] || '/dashboard');
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: colors.background }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-3xl shadow-floating p-8 md:p-12">
          <div className="text-center mb-6">
            {/* SYEP Logo */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-4"
            >
              <img 
                src={YAS_LOGO} 
                alt="Newark SYEP 2025" 
                className="w-full max-w-[240px] mx-auto"
                data-testid="syep-logo"
              />
            </motion.div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary tracking-tight mb-2">
              Join SYEP
            </h1>
            <p className="text-muted-foreground text-lg">Start your summer journey!</p>
          </div>

          <form onSubmit={handleRegister} className="space-y-5">
            <div data-testid="name-field">
              <Label htmlFor="name" className="text-sm font-semibold text-foreground mb-2 block">
                Full Name
              </Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="name"
                  data-testid="name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="pl-12 h-12 rounded-2xl"
                  required
                />
              </div>
            </div>

            <div data-testid="email-field">
              <Label htmlFor="email" className="text-sm font-semibold text-foreground mb-2 block">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your.email@example.com"
                  className="pl-12 h-12 rounded-2xl"
                  required
                />
              </div>
            </div>

            <div data-testid="password-field">
              <Label htmlFor="password" className="text-sm font-semibold text-foreground mb-2 block">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  data-testid="password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Create a password"
                  className="pl-12 h-12 rounded-2xl"
                  required
                />
              </div>
            </div>

            <div data-testid="role-field">
              <Label className="text-sm font-semibold text-foreground mb-2 block">
                I am a...
              </Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger data-testid="role-select" className="h-12 rounded-2xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="youth">Youth Participant</SelectItem>
                  <SelectItem value="lead_monitor">Lead Monitor</SelectItem>
                  <SelectItem value="coach">Coach / Supervisor</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.role === 'youth' && (
              <>
                <div data-testid="email-owner-field">
                  <Label className="text-sm font-semibold text-foreground mb-2 block">
                    This email belongs to:
                  </Label>
                  <Select value={formData.email_owner} onValueChange={(value) => setFormData({ ...formData, email_owner: value })}>
                    <SelectTrigger data-testid="email-owner-select" className="h-12 rounded-2xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="youth">Me (Youth)</SelectItem>
                      <SelectItem value="parent">My Parent</SelectItem>
                      <SelectItem value="both">Both of Us</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div data-testid="worksite-field">
                  <Label htmlFor="work_site" className="text-sm font-semibold text-foreground mb-2 block">
                    Work Site (Optional)
                  </Label>
                  <div className="relative">
                    <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="work_site"
                      data-testid="worksite-input"
                      value={formData.work_site}
                      onChange={(e) => setFormData({ ...formData, work_site: e.target.value })}
                      placeholder="Where are you working?"
                      className="pl-12 h-12 rounded-2xl"
                    />
                  </div>
                </div>
              </>
            )}

            <Button
              type="submit"
              data-testid="register-button"
              disabled={loading}
              className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-white font-bold text-lg shadow-button hover:shadow-lg transition-all active:scale-95 mt-6"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" data-testid="login-link" className="text-primary font-semibold hover:underline">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;